import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation } from "react-router";
import { api } from "../app/api.js";
import { apiErrorMessage } from "../api/client.js";
import { BackToLanding } from "../design/components/BackToLanding.js";
import { BrandMark } from "../design/components/BrandMark.js";
import { Button } from "../design/components/Button.js";
import { Alert } from "../design/components/Feedback.js";
import { Field, PasswordField } from "../design/components/Field.js";

const neutralMessage = "If an eligible account exists, a reset link has been sent.";

export function PasswordResetPage() {
  const query = new URLSearchParams(useLocation().search);
  const uid = query.get("uid") ?? "";
  const token = query.get("token") ?? "";
  const confirming = Boolean(uid && token);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (message || error) resultRef.current?.focus({ preventScroll: true });
  }, [message, error]);

  const submitRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true); setError(""); setMessage("");
    const data = new FormData(event.currentTarget);
    try {
      await api.requestPasswordReset(String(data.get("email")));
      setMessage(neutralMessage);
    } catch {
      setError("The request could not be completed. Please try again.");
    } finally { setBusy(false); }
  };

  const submitConfirmation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true); setError(""); setMessage("");
    const data = new FormData(event.currentTarget);
    const password = String(data.get("password"));
    if (password !== String(data.get("password_confirm"))) {
      setError("Passwords do not match."); setBusy(false); return;
    }
    try {
      await api.confirmPasswordReset(uid, token, password);
      setMessage("Your password has been changed. You can now log in.");
    } catch (reason) {
      setError(apiErrorMessage(reason, "This reset link is invalid or expired."));
    } finally { setBusy(false); }
  };

  return <main className="auth-page mobile-page-enter"><BackToLanding className="auth-back" /><section className="auth-card auth-card--login"><BrandMark className="auth-mark" /><div className="auth-heading"><h1>{confirming ? "Choose a new password" : "Reset your password"}</h1><p>{confirming ? "Use a strong password you do not use elsewhere." : "Enter your account email to request a secure reset link."}</p></div>{message ? <div ref={resultRef} tabIndex={-1}><Alert tone="success">{message}</Alert>{confirming ? <p className="password-reset-return"><a href="/login">Return to login</a></p> : null}</div> : confirming ? <form className="auth-form" onSubmit={submitConfirmation}><PasswordField label="New password" name="password" autoComplete="new-password" minLength={8} helper="Use at least 8 characters." required /><PasswordField label="Confirm new password" name="password_confirm" autoComplete="new-password" minLength={8} required />{error ? <div ref={resultRef} tabIndex={-1}><Alert tone="danger">{error}</Alert></div> : null}<Button type="submit" busy={busy}>Change password</Button></form> : <form className="auth-form" onSubmit={submitRequest}><Field label="Email address" name="email" type="email" autoComplete="email" required />{error ? <div ref={resultRef} tabIndex={-1}><Alert tone="danger">{error}</Alert></div> : null}<Button type="submit" busy={busy}>Send reset link</Button><p><a href="/login">Return to login</a></p></form>}</section><p className="auth-support">Need help? <a href="/enquire">Send an enquiry</a></p></main>;
}

